import { useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Flag, AlertTriangle, ChevronLeft, ChevronRight, Ban, AlertCircle, CheckCircle, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('admin_token');
const api = (method, url, data) => axios({ method, url: `${API_URL}/api${url}`, data, headers: { Authorization: `Bearer ${getToken()}` } });

const REPORT_CATEGORIES = {
  fake_profile: 'Fake Profile',
  harassment: 'Harassment',
  inappropriate_content: 'Inappropriate Content',
  spam: 'Spam',
  impersonation: 'Impersonation',
  other: 'Other',
};

export default function AdminModerationPage() {
  const [reports, setReports] = useState([]);
  const [flagged, setFlagged] = useState({ flagged_bios: [], flagged_messages: [] });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [resolveDialog, setResolveDialog] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [overview, setOverview] = useState({});

  const fetchReports = async (pg = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: statusFilter, page: pg, limit: 15 });
      const [reportsRes, flaggedRes, overviewRes] = await Promise.all([
        api('get', `/admin/moderation/reports?${params}`),
        api('get', '/admin/moderation/flagged-content'),
        api('get', '/admin/stats/overview'),
      ]);
      setReports(reportsRes.data.reports || []);
      setTotal(reportsRes.data.total || 0);
      setPages(reportsRes.data.pages || 1);
      setPage(pg);
      setFlagged(flaggedRes.data);
      setOverview(overviewRes.data);
    } catch (e) {
      toast.error('Failed to fetch moderation data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReports(); }, [statusFilter]);

  const handleResolve = async (action) => {
    if (!resolveDialog) return;
    try {
      await api('put', `/admin/moderation/reports/${resolveDialog.id}`, { action, notes: adminNotes });
      toast.success(`Report resolved: ${action}`);
      setResolveDialog(null);
      setAdminNotes('');
      fetchReports(page);
    } catch (e) {
      toast.error('Action failed');
    }
  };

  const statCards = [
    { icon: Flag, label: 'Pending Reports', value: overview.pending_reports || 0, color: 'text-red-600', bg: 'bg-red-100' },
    { icon: AlertTriangle, label: 'Flagged Content', value: overview.flagged_content || 0, color: 'text-[#F2A900]', bg: 'bg-[#F2A900]/10' },
    { icon: MessageCircle, label: 'Flagged Messages', value: overview.flagged_messages || 0, color: 'text-orange-600', bg: 'bg-orange-100' },
    { icon: AlertCircle, label: 'Pending KYC', value: overview.pending_kyc || 0, color: 'text-blue-600', bg: 'bg-blue-100' },
  ];

  return (
    <div data-testid="admin-moderation-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#2D2424]">Content Moderation</h1>
        <p className="text-sm text-[#78716C]">Manage reports and flagged content</p>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {statCards.map((s, i) => (
          <Card key={i} className="p-4 rounded-xl border border-[#E7E5E4] bg-white">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center`}>
                <s.icon className={`w-5 h-5 ${s.color}`} />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#2D2424]">{s.value}</div>
                <div className="text-xs text-[#78716C]">{s.label}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="reports" className="space-y-4">
        <TabsList className="bg-[#F9F5F0] rounded-full p-1">
          <TabsTrigger value="reports" className="rounded-full data-[state=active]:bg-[#D9381E] data-[state=active]:text-white">
            User Reports ({total})
          </TabsTrigger>
          <TabsTrigger value="flagged" className="rounded-full data-[state=active]:bg-[#D9381E] data-[state=active]:text-white">
            Flagged Content ({(flagged.flagged_bios?.length || 0) + (flagged.flagged_messages?.length || 0)})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="reports">
          <div className="flex justify-end mb-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Card className="rounded-xl border border-[#E7E5E4] bg-white overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-[#D9381E] border-t-transparent rounded-full animate-spin" /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#F9F5F0] border-b border-[#E7E5E4]">
                      <th className="text-left p-3 font-semibold">Reporter</th>
                      <th className="text-left p-3 font-semibold">Reported User</th>
                      <th className="text-left p-3 font-semibold">Category</th>
                      <th className="text-left p-3 font-semibold">Reason</th>
                      <th className="text-left p-3 font-semibold">Status</th>
                      <th className="text-left p-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map(r => (
                      <tr key={r.id} className="border-b border-[#E7E5E4]/50 hover:bg-[#F9F5F0]/50">
                        <td className="p-3 font-medium">{r.reporter_name || '-'}</td>
                        <td className="p-3 font-medium text-[#D9381E]">{r.reported_name || r.reported_id?.slice(0, 8)}</td>
                        <td className="p-3"><Badge className="bg-[#F9F5F0] text-[#2D2424] text-[10px]">{REPORT_CATEGORIES[r.category] || r.category}</Badge></td>
                        <td className="p-3 text-[#78716C] max-w-[200px] truncate">{r.reason}</td>
                        <td className="p-3">
                          <Badge className={r.status === 'pending' ? 'bg-[#F2A900]/20 text-[#F2A900]' : 'bg-green-100 text-green-700'}>{r.status}</Badge>
                        </td>
                        <td className="p-3">
                          {r.status === 'pending' && (
                            <Button size="sm" variant="outline" onClick={() => setResolveDialog(r)} className="rounded-lg text-xs" data-testid={`resolve-report-${r.id}`}>
                              Resolve
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {reports.length === 0 && (
                      <tr><td colSpan={6} className="p-12 text-center text-[#78716C]">No reports found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            {pages > 1 && (
              <div className="flex items-center justify-between p-4 border-t border-[#E7E5E4]">
                <span className="text-xs text-[#78716C]">Page {page} of {pages}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => fetchReports(page - 1)} disabled={page <= 1}><ChevronLeft className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => fetchReports(page + 1)} disabled={page >= pages}><ChevronRight className="w-4 h-4" /></Button>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="flagged">
          <div className="space-y-4">
            {flagged.flagged_bios?.length > 0 && (
              <Card className="p-5 rounded-xl border border-[#E7E5E4] bg-white">
                <h3 className="text-sm font-semibold text-[#2D2424] mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-[#F2A900]" /> Flagged Bios
                </h3>
                <div className="space-y-3">
                  {flagged.flagged_bios.map((u, i) => (
                    <div key={i} className="p-3 bg-[#F9F5F0] rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{u.name}</span>
                        <Badge className="bg-red-100 text-red-700 text-[10px]">Flagged</Badge>
                      </div>
                      <p className="text-sm text-[#78716C]">{u.bio}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}
            {flagged.flagged_messages?.length > 0 && (
              <Card className="p-5 rounded-xl border border-[#E7E5E4] bg-white">
                <h3 className="text-sm font-semibold text-[#2D2424] mb-3 flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-orange-500" /> Flagged Messages
                </h3>
                <div className="space-y-2">
                  {flagged.flagged_messages.map((m, i) => (
                    <div key={i} className="p-3 bg-[#F9F5F0] rounded-lg flex items-center justify-between">
                      <div>
                        <p className="text-sm text-[#78716C]">{m.content}</p>
                        <p className="text-xs text-[#78716C] mt-1">{new Date(m.created_at).toLocaleString()}</p>
                      </div>
                      <Badge className="bg-orange-100 text-orange-700 text-[10px]">Profanity</Badge>
                    </div>
                  ))}
                </div>
              </Card>
            )}
            {(flagged.flagged_bios?.length === 0 && flagged.flagged_messages?.length === 0) && (
              <Card className="p-12 text-center rounded-xl border border-[#E7E5E4]">
                <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
                <p className="text-[#78716C]">No flagged content. All clear!</p>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Resolve Dialog */}
      <Dialog open={!!resolveDialog} onOpenChange={() => { setResolveDialog(null); setAdminNotes(''); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Resolve Report</DialogTitle></DialogHeader>
          {resolveDialog && (
            <div className="space-y-3">
              <div className="p-3 bg-[#F9F5F0] rounded-lg text-sm">
                <p><span className="font-medium">Category:</span> {REPORT_CATEGORIES[resolveDialog.category]}</p>
                <p><span className="font-medium">Reason:</span> {resolveDialog.reason}</p>
                {resolveDialog.details && <p><span className="font-medium">Details:</span> {resolveDialog.details}</p>}
              </div>
              <Textarea value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} placeholder="Admin notes..." rows={2} />
              <DialogFooter className="gap-2">
                <Button onClick={() => handleResolve('dismiss')} variant="outline" className="rounded-lg">Dismiss</Button>
                <Button onClick={() => handleResolve('warn')} className="bg-[#F2A900] text-[#2D2424] rounded-lg">
                  <AlertTriangle className="w-4 h-4 mr-1" /> Warn User
                </Button>
                <Button onClick={() => handleResolve('ban')} className="bg-red-600 text-white rounded-lg" data-testid="ban-user-btn">
                  <Ban className="w-4 h-4 mr-1" /> Ban User
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
